import * as Phaser from 'phaser';
import { PaintedScene } from '../PaintedScene';
import {
  installSnowTexture,
  installBreathTexture,
  createSnowfall,
  createBreathMist,
} from './frostmereAmbient';

export const FROST_BRIDGE_PHASER_KEY = 'FrostBridgeScene' as const;
export const FROST_BRIDGE_MANIFEST_KEY = 's-301-frost-bridge' as const;

const MIST_TEXTURE_KEY = '__frost_gorge_mist__';
const TILT_DEGREES = 5;
const MIDSPAN_X = 0.5;
const TILT_RANGE = 0.15;
const COLOR_GRADE_TINT = 0xc8d8f0;

export class FrostBridgeScene extends PaintedScene {
  private snowEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private breathEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private mistEmitters: Phaser.GameObjects.Particles.ParticleEmitter[] = [];
  private colorGradeOverlay: Phaser.GameObjects.Rectangle | null = null;
  private tiltTween: Phaser.Tweens.Tween | null = null;
  private currentTiltDir: 'left' | 'center' | 'right' = 'center';

  constructor() {
    super(FROST_BRIDGE_PHASER_KEY, FROST_BRIDGE_MANIFEST_KEY);
  }

  preload(): void {
    super.preload();
    installSnowTexture(this);
    installBreathTexture(this);
    this.installMistTexture();
  }

  protected override onSceneReady(): void {
    this.buildColorGradeOverlay();
    this.buildGorgeMist();
    this.snowEmitter = createSnowfall(this, 0.8);

    if (this.walker) {
      this.breathEmitter = createBreathMist(this, this.walker);
    }
  }

  update(time: number, delta: number): void {
    super.update(time, delta);
    this.tickCameraTilt();
  }

  protected override onSceneShutdown(): void {
    this.snowEmitter?.destroy();
    this.breathEmitter?.destroy();
    for (const e of this.mistEmitters) e.destroy();
    this.colorGradeOverlay?.destroy();
    this.tiltTween?.destroy();
    this.snowEmitter = null;
    this.breathEmitter = null;
    this.mistEmitters = [];
    this.colorGradeOverlay = null;
    this.tiltTween = null;
    this.currentTiltDir = 'center';
  }

  private installMistTexture(): void {
    if (this.textures.exists(MIST_TEXTURE_KEY)) return;
    const SIZE = 32;
    const HALF = SIZE / 2;
    const gfx = this.make.graphics({ x: 0, y: 0 }, false);
    for (let r = HALF; r >= 1; r--) {
      const alpha = (1 - r / HALF) ** 2 * 0.4;
      gfx.fillStyle(0xdce8f4, alpha);
      gfx.fillCircle(HALF, HALF, r);
    }
    gfx.generateTexture(MIST_TEXTURE_KEY, SIZE, SIZE);
    gfx.destroy();
  }

  private buildColorGradeOverlay(): void {
    const w = this.scale.width;
    const h = this.scale.height;
    this.colorGradeOverlay = this.add.rectangle(w / 2, h / 2, w, h, COLOR_GRADE_TINT, 0.08);
    this.colorGradeOverlay.setScrollFactor(0);
    this.colorGradeOverlay.setDepth(140);
    this.colorGradeOverlay.setBlendMode(Phaser.BlendModes.MULTIPLY);
  }

  private buildGorgeMist(): void {
    const w = this.scale.width;
    const h = this.scale.height;
    const gorgeY = h * 0.78;

    const configs: Array<{
      y: number; speedY: { min: number; max: number }; speedX: { min: number; max: number };
      alpha: { start: number; end: number }; scale: { min: number; max: number };
      lifespan: number; frequency: number; depth: number;
    }> = [
      {
        y: gorgeY + 20, speedY: { min: -8, max: -2 }, speedX: { min: -30, max: 30 },
        alpha: { start: 0.15, end: 0 }, scale: { min: 1.5, max: 3.0 },
        lifespan: 5000, frequency: 200, depth: 2,
      },
      {
        y: gorgeY, speedY: { min: -12, max: -4 }, speedX: { min: -20, max: 20 },
        alpha: { start: 0.2, end: 0 }, scale: { min: 1.0, max: 2.5 },
        lifespan: 4000, frequency: 300, depth: 4,
      },
      {
        y: gorgeY - 15, speedY: { min: -6, max: -1 }, speedX: { min: -40, max: 40 },
        alpha: { start: 0.1, end: 0 }, scale: { min: 2.0, max: 4.0 },
        lifespan: 6000, frequency: 250, depth: 6,
      },
    ];

    for (const cfg of configs) {
      const emitter = this.add.particles(0, 0, MIST_TEXTURE_KEY, {
        x: { min: -30, max: w + 30 },
        y: cfg.y,
        speedX: cfg.speedX,
        speedY: cfg.speedY,
        alpha: cfg.alpha,
        scale: cfg.scale,
        lifespan: cfg.lifespan,
        frequency: cfg.frequency,
        quantity: 1,
        maxAliveParticles: 30,
        blendMode: Phaser.BlendModes.SCREEN,
        advance: 3000,
      });
      emitter.setScrollFactor(0);
      emitter.setDepth(cfg.depth);
      this.mistEmitters.push(emitter);
    }
  }

  private tickCameraTilt(): void {
    const nx = this.walkerNX;
    let targetDir: 'left' | 'center' | 'right';

    if (nx < MIDSPAN_X - TILT_RANGE) {
      targetDir = 'left';
    } else if (nx > MIDSPAN_X + TILT_RANGE) {
      targetDir = 'right';
    } else {
      targetDir = 'center';
    }

    if (targetDir === this.currentTiltDir) return;
    this.currentTiltDir = targetDir;

    const targetAngle = targetDir === 'center' ? 0
      : targetDir === 'right' ? TILT_DEGREES
      : -TILT_DEGREES;

    this.tiltTween?.destroy();
    this.tiltTween = this.tweens.add({
      targets: this.cameras.main,
      rotation: Phaser.Math.DegToRad(targetAngle),
      duration: 1200,
      ease: 'Sine.easeInOut',
    });
  }
}
